import { ApolloError } from '@apollo/client';
import { UUID } from '@cmg/common';
import defaultTo from 'lodash/defaultTo';
import isNil from 'lodash/isNil';
import React from 'react';

import usePollingWhenVisible from '../../../../../common/hooks/usePollingWhenVisible';
import { useGetPublishedOfferingEntitlementsAndCheckInstitutionalIndicationAccess } from '../../../../../common/util/check-access/useGetPublishedOfferingEntitlementsAndCheckInstitutionalIndicationAccess';
import { isValidCmgEntityKey } from '../../../../../common/util/isValidCmgEntityKey';
import { canSyndicateManagerUpdateInstitutionalIndication } from '../../../../../common/util/offering/syndicate-entitlements';
import { CertificateType, OrderBookConfiguration } from '../../../../../graphql';
import { OrderBookState } from '../../../../../graphql/shared-types';
import {
  getCertificateType,
  isReg144A,
} from '../../compliance/context/hooks/certificates/Certificates.model';
import { useSalesAndTradingCertificatesModel } from '../../compliance/context/hooks/certificates/sale-and-trading/useCertificatesSalesAndTradingModel';
import {
  Attestation,
  certificateModelDefaults,
  ComplianceWarning,
} from '../../compliance/context/hooks/certificates/useCertificatesModel';
import { OrderBookPersonaType } from '../../hooks/useIdentifyOrderBookPersona';
import { CrmSelection_AllInvestorFirmsMappedToCmgEntityKeyQuery } from '../components/crm-selection/graphql/__generated__';
import { useInvestorFirmModel } from './hooks/investorFirm/useInvestorFirmModel';
import {
  SalesAndTradingIndicationQuery,
  useSalesAndTradingIndicationQuery,
} from './hooks/sales-and-trading/__generated__';
import { useIndicationActivityOrderBookSalesStateForSalesTraderQuery } from './hooks/sales-and-trading/useIndicationActivityOrderBookSalesStateForSalesTraderQuery';
import { SyndicateIndication_PublishedOfferings_OfferingPartsFragment } from './hooks/syndicate/__generated__';

type AccessChecks = {
  canReadOfferingAndIndication: boolean;
  canUpdateCoveredIndication: boolean;
  canBillingAndDeliveryAgentUpdateAllocationAcknowledgment: boolean;
};

export type IndicationActivitySalesAndTradingContextType = {
  offeringId: UUID;
  loading: boolean;
  error?: ApolloError;
  basicPublishedOffering?: SalesAndTradingIndicationQuery['basicPublishedOffering'];
  publishedOffering: SyndicateIndication_PublishedOfferings_OfferingPartsFragment | null;
  coveredIndication?: Exclude<SalesAndTradingIndicationQuery['coveredIndication'], undefined>;
  complianceWarning: ComplianceWarning;
  attestation: Attestation | null;
  allInvestorFirmsMappedToCmgEntityKey?: CrmSelection_AllInvestorFirmsMappedToCmgEntityKeyQuery['allInvestorFirmsMappedToCmgEntityKey'];
  access: AccessChecks;
  orderBookSalesState?: OrderBookState;
  certificateType: CertificateType | null;
  orderBookConfiguration: Pick<
    OrderBookConfiguration,
    | 'isLimitIndicationsOnlyEnabled'
    | 'isIndicationPriceAsPercentageParEnabled'
    | 'isOnlySharesUnitOnIndicationsEnabled'
  >;
};

export const IndicationActivitySalesAndTradingContext =
  React.createContext<IndicationActivitySalesAndTradingContextType>({
    offeringId: '',
    publishedOffering: null,
    loading: false,
    complianceWarning: certificateModelDefaults.warning,
    access: {
      canReadOfferingAndIndication: false,
      canUpdateCoveredIndication: false,
      canBillingAndDeliveryAgentUpdateAllocationAcknowledgment: false,
    },
    attestation: null,
    certificateType: null,
    orderBookConfiguration: {
      isLimitIndicationsOnlyEnabled: false,
      isIndicationPriceAsPercentageParEnabled: false,
      isOnlySharesUnitOnIndicationsEnabled: false,
    },
  });

export const useIndicationActivitySalesAndTradingContext = () => {
  const context = React.useContext(IndicationActivitySalesAndTradingContext);

  if (isNil(context)) {
    throw new Error('No provider found for IndicationActivitySalesAndTradingContext');
  }

  return context;
};

export type IndicationActivitySalesAndTradingContextProps = {
  offeringId: UUID;
  cmgEntityKey: UUID | undefined;
  indicationId: UUID;
};

export const IndicationActivitySalesAndTradingContextProvider: React.FC<
  React.PropsWithChildren<IndicationActivitySalesAndTradingContextProps>
> = ({ children, offeringId, cmgEntityKey, indicationId }) => {
  const {
    canRead: canReadOfferingAndIndication,
    canUpdate: canUpdateCoveredIndication,
    loading: indicationAccessLoading,
  } = useGetPublishedOfferingEntitlementsAndCheckInstitutionalIndicationAccess({ offeringId });
  const {
    loading: isLoadingOffering,
    data,
    error,
    startPolling,
    stopPolling,
    called,
  } = useSalesAndTradingIndicationQuery({
    variables: {
      offeringId,
      cmgEntityKey: cmgEntityKey!,
      indicationId,
      skipIndicationPart: !isValidCmgEntityKey(cmgEntityKey),
    },
    skip: !cmgEntityKey || !offeringId || !canReadOfferingAndIndication,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const {
    data: obSalesStateChangedData,
    loading: obSalesStateChangedLoading,
    error: obSalesStateChangedError,
  } = useIndicationActivityOrderBookSalesStateForSalesTraderQuery(
    {
      offeringId,
    },
    cmgEntityKey,
    canReadOfferingAndIndication
  );

  const offering = defaultTo(data?.publishedOfferings?.items?.[0], null);
  const instruments = offering?.instruments ?? [];
  const offeringType = offering?.type;
  const isReg144AValue = instruments[0] ? isReg144A(instruments[0]) : false;
  const certificateType = getCertificateType(offeringType, isReg144AValue);

  usePollingWhenVisible({ pollInterval: 5_000, startPolling, stopPolling, called });

  const {
    loading: isLoadingCertificates,
    warning: complianceWarning,
    attestation,
  } = useSalesAndTradingCertificatesModel({
    type: OrderBookPersonaType.SALES_AND_TRADING,
    offeringId,
    cmgEntityKey,
    certificateType,
    isReg144A: isReg144AValue,
    enforceValidAttestationForSalesAndTrading:
      obSalesStateChangedData?.salesAndTradingOrderBook
        ?.enforceValidAttestationForSalesAndTrading ?? true,
    offeringType,
  });
  const { loading: isLoadingInvestorFirm, allInvestorFirmsMappedToCmgEntityKey } =
    useInvestorFirmModel({
      type: OrderBookPersonaType.SALES_AND_TRADING,
      cmgEntityKey,
    });

  const context = React.useMemo<IndicationActivitySalesAndTradingContextType>(
    () => ({
      offeringId,
      loading:
        isLoadingOffering ||
        isLoadingCertificates ||
        isLoadingInvestorFirm ||
        indicationAccessLoading ||
        obSalesStateChangedLoading,
      error: error || obSalesStateChangedError,
      basicPublishedOffering: data?.basicPublishedOffering,
      publishedOffering: offering,
      coveredIndication: data?.coveredIndication,
      complianceWarning,
      attestation,
      allInvestorFirmsMappedToCmgEntityKey,
      access: {
        canReadOfferingAndIndication,
        canUpdateCoveredIndication,
        canBillingAndDeliveryAgentUpdateAllocationAcknowledgment:
          canSyndicateManagerUpdateInstitutionalIndication({
            cmgEntityKey: data?.coveredIndication?.billingAndDeliveryAgentCmgEntityKey,
            syndicatesEntitlements: offering?.syndicate?.managers ?? [],
          }),
      },
      orderBookSalesState: obSalesStateChangedData?.salesAndTradingOrderBook?.salesState,
      certificateType,
      orderBookConfiguration: {
        isLimitIndicationsOnlyEnabled:
          !!offering?.orderBookConfiguration?.isLimitIndicationsOnlyEnabled,
        isIndicationPriceAsPercentageParEnabled:
          !!offering?.orderBookConfiguration?.isIndicationPriceAsPercentageParEnabled,
        isOnlySharesUnitOnIndicationsEnabled:
          !!offering?.orderBookConfiguration?.isOnlySharesUnitOnIndicationsEnabled,
      },
    }),
    [
      offeringId,
      isLoadingOffering,
      isLoadingCertificates,
      isLoadingInvestorFirm,
      indicationAccessLoading,
      obSalesStateChangedLoading,
      error,
      obSalesStateChangedError,
      data?.basicPublishedOffering,
      data?.coveredIndication,
      offering,
      complianceWarning,
      attestation,
      allInvestorFirmsMappedToCmgEntityKey,
      canReadOfferingAndIndication,
      canUpdateCoveredIndication,
      obSalesStateChangedData?.salesAndTradingOrderBook?.salesState,
      certificateType,
    ]
  );

  return (
    <IndicationActivitySalesAndTradingContext.Provider value={context}>
      {children}
    </IndicationActivitySalesAndTradingContext.Provider>
  );
};
